import { REPLAY_EVENTS } from '../workspaceReplay2/utils/replayAnnotations'

// Turns a Reasoning Gap student's telemetry into the events the shared replay player reads
// (workspaceReplay2/hooks/useReplayState.js), so View Student Data works for GapCode /
// GapThinking sessions. ReasoningGapStudent logs its own event types (GAP_RUN, GAP_SNAPSHOT,
// ...) and keeps the chat text, hidden-test results and hints in the reasoning_gap_* tables,
// so those are joined in from the instructor submissions endpoint (listSubmissions).
//
// Timing: telemetry carries the student's browser clock, the tables the server's. A row that
// matches a telemetry event (a question by its text, an attempt by its number) takes that
// event's time; any other row is shifted by the median browser-minus-server difference of
// those matches.

const AWAY_MARKER_MS = 10000
const PASTE_SNIPPET = 60

// Events logged the moment the student acts on the open page, so they end any away period
// whose closing focus/visibility event was missed (e.g. a reload logs no TAB_STATE visible).
// Not GAP_RUN: it is stamped when the run finishes, which can be after the student left.
const PRESENT_EVENTS = new Set(['GAP_SESSION_START', 'GAP_SUBMIT', 'CHAT_QUERY', 'CODE_PASTE', 'CHAT_PASTE'])

// Telemetry from before revise-and-resubmit (Oct 5) has no attempt numbers; there was only
// one attempt then.
const attemptOf = (e) => e.payload?.attempt || 1

const PASTE_TARGETS = { code: 'the code editor', explanation: 'the explanation' }
const KEYSTROKE_EVENTS = new Set(['GAP_EDIT', 'GAP_EDITOR_TEXT', 'CURSOR_SELECT', 'EDITOR_KEY', 'GAP_KEYSTROKES_DROPPED'])

const ms = (value) => (value ? new Date(value).getTime() : NaN)
const byTime = (a, b) => a.timestamp - b.timestamp

function median (values) {
  if (!values.length) return 0
  const sorted = [...values].sort((a, b) => a - b)
  const mid = sorted.length >> 1
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

function formatDuration (millis) {
  const seconds = Math.round(millis / 1000)
  return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`
}

const marker = (timestamp, kind, label) => ({ type: REPLAY_EVENTS.MARKER, timestamp, payload: { kind, label } })
const notice = (timestamp, mode, title, lines = [], items = []) => ({ type: REPLAY_EVENTS.NOTICE, timestamp, payload: { mode, title, lines, items } })

// Earlier attempts (archived rows) plus the current submission row, oldest first.
function attemptsOf (submission) {
  if (!submission) return []
  const byAttempt = new Map()
  for (const row of [...(submission.history || []), submission]) {
    byAttempt.set(row.attempt || 1, { ...row, attempt: row.attempt || 1 })
  }
  return [...byAttempt.values()].sort((a, b) => a.attempt - b.attempt)
}

// Pairs each stored question with the CHAT_QUERY that sent it: same text, in order, and
// among identical texts the one sent closest to when the server stored the question (with
// `offset`, once known), so an identical retry isn't confused with an earlier copy that
// failed before reaching the server. Queries the server never stored (refused, or failed on
// the way) stay unmatched. Returns question row id -> query index.
function matchQueries (questions, queries, offset = null) {
  const matched = new Map()
  let next = 0
  for (const question of questions) {
    const candidates = []
    for (let k = next; k < queries.length; k++) {
      if ((queries[k].payload?.message || '').trim() === question.content) candidates.push(k)
    }
    if (!candidates.length) continue
    let pick = candidates[0]
    if (offset !== null) {
      const storedAt = ms(question.created_at) + offset
      for (const k of candidates) {
        if (Math.abs(queries[k].timestamp - storedAt) < Math.abs(queries[pick].timestamp - storedAt)) pick = k
      }
    }
    matched.set(question.id, pick)
    next = pick + 1
  }
  return matched
}

// The response or error the page logged for each query, before the next query was sent.
function outcomesOf (events, queries) {
  return queries.map((query, qi) => {
    const end = qi + 1 < queries.length ? queries[qi + 1].timestamp : Infinity
    const within = (e) => e.timestamp >= query.timestamp && e.timestamp <= end
    return {
      response: events.find(e => e.type === 'CHAT_RESPONSE' && within(e)),
      error: events.find(e => e.type === 'CHAT_ERROR' && within(e)),
    }
  })
}

function noReplyText (status) {
  if (status === 409) return '[No reply] The assistant was closed at this point (an attempt was being checked, or the quiz was finished).'
  if (status === 429) return '[No reply] The student had reached the question limit.'
  if (status === 413) return '[No reply] The question was too long.'
  return '[No reply] The assistant was unavailable.'
}

function chatEvents (rows, queries, outcomes, matched, offset) {
  const out = []
  const ask = (timestamp, message) => out.push({ type: 'CHAT_QUERY', timestamp, payload: { message } })
  const answer = (timestamp, content, noReply = false) => out.push({ type: 'CHAT_RESPONSE', timestamp, payload: { content, noReply } })
  const replies = rows.filter(r => r.role === 'assistant')
  const claimed = new Set()
  // The reply stored right after a question: the usual case.
  const nextReply = (question) => {
    const row = rows[rows.indexOf(question) + 1]
    return row && row.role === 'assistant' && !claimed.has(row.id) ? row : null
  }
  // The reply the page actually received, by the length it logged: a late reply to an
  // earlier, timed-out question can be stored between a question and its own reply.
  const receivedReply = (question, response) => {
    const length = response?.payload?.length
    const byLength = Number.isFinite(length) &&
      replies.find(r => r.id > question.id && !claimed.has(r.id) && (r.content || '').length === length)
    return byLength || nextReply(question)
  }

  for (const question of rows.filter(r => r.role === 'user')) {
    const qi = matched.get(question.id)
    if (qi === undefined) {
      const askedAt = ms(question.created_at) + offset
      if (!Number.isFinite(askedAt)) continue
      ask(askedAt, question.content)
      const reply = nextReply(question)
      if (reply) {
        claimed.add(reply.id)
        answer(Math.max(askedAt, ms(reply.created_at) + offset), reply.content)
      } else {
        answer(askedAt, noReplyText(), true)
      }
      continue
    }
    const askedAt = queries[qi].timestamp
    const { response, error } = outcomes[qi]
    ask(askedAt, question.content)
    // A logged error means the page never showed a reply, even if the server stored one
    // later (e.g. the request timed out).
    const reply = response || !error ? receivedReply(question, response) : null
    if (reply) {
      claimed.add(reply.id)
      answer(response ? response.timestamp : askedAt + Math.max(0, ms(reply.created_at) - ms(question.created_at)), reply.content)
    } else {
      answer(error ? error.timestamp : askedAt, noReplyText(error?.payload?.status), true)
    }
  }

  const used = new Set(matched.values())
  queries.forEach((query, qi) => {
    if (used.has(qi)) return
    const { error } = outcomes[qi]
    ask(query.timestamp, (query.payload?.message || '').trim())
    answer(error ? error.timestamp : query.timestamp, noReplyText(error?.payload?.status), true)
  })
  return out
}

// The student's Run button: their own file only, never the hidden tests. GAP_RUN is
// stamped when the run finishes, and the student may have edited the code meanwhile, so the
// code that ran travels as `ranCode` (for the Code tab) rather than `code`, which the replay
// would show as the editor's content. Snapshots already record what was on screen.
function runEvents (events) {
  const out = []
  for (const e of events) {
    if (e.type !== 'GAP_RUN') continue
    const { code, output, ok } = e.payload || {}
    out.push({ type: 'TERMINAL_RUN', timestamp: e.timestamp, payload: { ranCode: code, terminalLine: '' } })
    if (output) out.push({ type: 'TERMINAL_OUTPUT', timestamp: e.timestamp, payload: { output, stream: ok ? 'stdout' : 'stderr' } })
  }
  return out
}

// Raw events as the replay should read them: chat comes from chatEvents, a run's code is not
// on-screen code (see runEvents), and what a submit captured is shown just before the
// 'Submitted' screen covers the editor (typing within the last ~4 s has no snapshot yet).
function passThrough (events) {
  const out = []
  for (const e of events) {
    if (e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE') continue
    // Keystroke-level records (from 2026-10-29): the replay steps between snapshots, so they
    // are left out.
    if (KEYSTROKE_EVENTS.has(e.type)) continue
    if (e.type === 'GAP_RUN') {
      const { code, ...rest } = e.payload || {}
      out.push({ ...e, payload: { ...rest, ranCode: code } })
    } else if (e.type === 'GAP_SUBMIT' && (e.payload?.code !== undefined || typeof e.payload?.explanation === 'string')) {
      out.push({ type: 'GAP_SNAPSHOT', timestamp: e.timestamp - 1, payload: { code: e.payload.code, explanation: e.payload.explanation, atSubmit: true } })
      out.push(e)
    } else {
      out.push(e)
    }
  }
  return out
}

function testPayload (attempt, tests) {
  const results = attempt.test_results
  const byTestList = tests.length > 0 && results.every(r => tests.some(t => t.id === r.id))
  const rows = byTestList
    ? tests.map(t => ({ passed: !!results.find(r => r.id === t.id)?.passed }))
    : results.map(r => ({ passed: !!r.passed }))
  const passed = rows.filter(r => r.passed).length
  return {
    attempt: attempt.attempt,
    // Hidden tests run in the background after a submit; the student never saw them in the
    // output panel, only as a count on the feedback screen.
    terminalLine: '',
    passed_count: passed,
    total_tests: rows.length,
    passrate: rows.length ? Math.round((passed / rows.length) * 100) : 0,
    detailed_results: rows.map((r, i) => ({ test_case_number: i + 1, passed: r.passed })),
  }
}

const testsLineOf = (attempt) => (attempt && attempt.tests_total != null
  ? `Hidden inputs: ${attempt.tests_passed} of ${attempt.tests_total} handled correctly`
  : null)
const hintsOf = (attempt) => ((attempt && attempt.student_hints && attempt.student_hints.hints) || []).map(h => h.hint).filter(Boolean)

// Submissions, hidden-test results and the screens around them (feedback, revising, survey).
function attemptEvents (events, attempts, submits, tests, offset) {
  const out = []
  const submittedAt = attempts.map(a => submits.get(a.attempt)?.timestamp ?? ms(a.submitted_at) + offset)
  const views = events.filter(e => e.type === 'GAP_FEEDBACK_VIEWED')
  // Since Oct 5 the page logs (and immediately sends) a view whenever an attempt's feedback
  // is on screen, so no view means the student never saw it. Older telemetry has no attempt
  // numbers and may lack views; only there is the server's analysis time used as an estimate.
  const tagged = events.some(e => (e.type === 'GAP_SUBMIT' || e.type === 'GAP_FEEDBACK_VIEWED') && e.payload?.attempt != null)

  attempts.forEach((attempt, j) => {
    const at = submittedAt[j]
    if (!Number.isFinite(at)) return
    const until = submittedAt.slice(j + 1).find(Number.isFinite) ?? Infinity
    const label = `attempt ${attempt.attempt}`

    out.push(marker(at, 'submit', `Submitted ${label}`))
    out.push(notice(at, 'screen', `Submitted ${label}`, [
      attempt.status === 'analysis_failed'
        ? 'Feedback could not be generated for this attempt.'
        : 'Checking the code against hidden inputs, then preparing feedback…',
    ]))

    if (Array.isArray(attempt.test_results) && attempt.test_results.length > 0) {
      const ran = events.find(e => e.type === 'GAP_TESTS_RUN' && e.timestamp >= at && e.timestamp < until)
      const ranAt = ran ? ran.timestamp : Math.max(at, ms(attempt.tested_at) + offset)
      if (Number.isFinite(ranAt)) out.push({ type: 'TEST_CASE_RESULT', timestamp: ranAt, payload: testPayload(attempt, tests) })
    }

    if (attempt.status === 'analyzed') {
      // Every view, not just the first: a reload back onto the feedback screen logs another.
      let shownAt = views.filter(e => attemptOf(e) === attempt.attempt).map(e => e.timestamp)
      if (!shownAt.length && !tagged) {
        const estimate = Math.max(at, ms(attempt.analyzed_at) + offset)
        const leftAt = events.find(e => e.timestamp > at && ['GAP_REVISE_START', 'GAP_FINISH_CHOSEN', 'GAP_SURVEY_SUBMITTED'].includes(e.type))?.timestamp ?? until
        if (estimate < leftAt) shownAt = [estimate]
      }
      const hints = hintsOf(attempt)
      for (const time of shownAt) {
        out.push(notice(time, 'screen', `Feedback on ${label}`,
          [testsLineOf(attempt), attempt.student_hints?.overall_message, hints.length ? null : 'No gaps found.'].filter(Boolean),
          hints))
      }
    }
  })

  for (const e of events) {
    if (e.type === 'GAP_REVISE_START') {
      const from = attempts.find(a => a.attempt === e.payload?.fromAttempt)
      const next = (e.payload?.fromAttempt || 0) + 1
      out.push(notice(e.timestamp, 'banner', `Attempt ${next}: revising after feedback`, [testsLineOf(from)].filter(Boolean), hintsOf(from)))
      out.push({ type: REPLAY_EVENTS.TERMINAL_CLEAR, timestamp: e.timestamp, payload: {} })
    } else if (e.type === 'GAP_SESSION_START') {
      // A (re)load starts with an empty output panel.
      out.push({ type: REPLAY_EVENTS.TERMINAL_CLEAR, timestamp: e.timestamp, payload: {} })
    } else if (e.type === 'GAP_FINISH_CHOSEN') {
      out.push(notice(e.timestamp, 'screen', 'Finishing', ['The student chose to finish and is answering the closing survey.']))
    } else if (e.type === 'GAP_SURVEY_SUBMITTED') {
      out.push(notice(e.timestamp, 'screen', 'Finished', ['The student submitted the closing survey.']))
    }
  }
  return out
}

// Hidden tab or another window in front, as one away period per stretch. Periods of at least
// AWAY_MARKER_MS also get a timeline marker.
function awayEvents (events) {
  const out = []
  let hidden = false
  let blurred = false
  let current = null
  for (const e of events) {
    if (e.type === 'TAB_STATE') hidden = e.payload?.visible === false
    else if (e.type === 'GAP_WINDOW_BLUR') blurred = true
    else if (e.type === 'GAP_WINDOW_FOCUS') blurred = false
    else if (PRESENT_EVENTS.has(e.type)) { hidden = false; blurred = false }
    else continue

    const reason = hidden ? 'tab' : blurred ? 'window' : null
    if (reason === (current ? current.reason : null)) continue
    out.push({ type: REPLAY_EVENTS.AWAY, timestamp: e.timestamp, payload: { away: !!reason, reason } })
    if (reason && current) {
      current.reason = reason
    } else if (reason) {
      current = { reason, since: e.timestamp }
    } else {
      const length = e.timestamp - current.since
      if (length >= AWAY_MARKER_MS) {
        out.push(marker(current.since, 'away', `Away from the quiz page for ${formatDuration(length)}`))
      }
      current = null
    }
  }
  return out
}

function activityMarkers (events) {
  const out = []
  for (const e of events) {
    const p = e.payload || {}
    if (e.type === 'CODE_PASTE' || e.type === 'CHAT_PASTE') {
      const where = e.type === 'CHAT_PASTE' ? 'the chat' : PASTE_TARGETS[p.target] || 'the page'
      const text = String(p.text || '').replace(/\s+/g, ' ').trim()
      const snippet = text.length > PASTE_SNIPPET ? `${text.slice(0, PASTE_SNIPPET)}…` : text
      out.push(marker(e.timestamp, 'paste', `Pasted ${p.length ?? text.length} characters into ${where}${snippet ? `: "${snippet}"` : ''}`))
    } else if (e.type === 'CHAT_COPY_HISTORY') {
      out.push(marker(e.timestamp, 'copy', `Copied ${p.length ?? 0} characters from the chat`))
    }
  }
  return out
}

// rawEvents: one student's telemetry events. submission: their row from listSubmissions (with
// `history`), chat: their rows from listSubmissions' `chats`, tests: listSubmissions' `tests`.
export function adaptReasoningGapEvents (rawEvents, { submission = null, chat = [], tests = [] } = {}) {
  const events = (rawEvents || []).filter(e => e && Number.isFinite(e.timestamp)).sort(byTime)
  const rows = [...(chat || [])].sort((a, b) => a.id - b.id)
  const questions = rows.filter(r => r.role === 'user')
  const queries = events.filter(e => e.type === 'CHAT_QUERY')
  const outcomes = outcomesOf(events, queries)
  const attempts = attemptsOf(submission)
  const submits = new Map()
  for (const e of events) if (e.type === 'GAP_SUBMIT') submits.set(attemptOf(e), e)

  const createdAt = new Map(questions.map(q => [q.id, ms(q.created_at)]))
  const offsetOf = (matches) => median([
    ...[...matches].map(([id, qi]) => queries[qi].timestamp - createdAt.get(id)),
    ...attempts.map(a => (submits.has(a.attempt) ? submits.get(a.attempt).timestamp - ms(a.submitted_at) : NaN)),
  ].filter(Number.isFinite))
  // Learn the clock offset from unambiguous matches (texts sent once) and submits, match
  // again with it to settle identical texts, then take the offset from all matches.
  const textOf = (query) => (query.payload?.message || '').trim()
  const sends = new Map()
  for (const query of queries) sends.set(textOf(query), (sends.get(textOf(query)) || 0) + 1)
  const unambiguous = [...matchQueries(questions, queries)].filter(([, qi]) => sends.get(textOf(queries[qi])) === 1)
  const matched = matchQueries(questions, queries, offsetOf(unambiguous))
  const offset = offsetOf(matched)

  return [
    ...passThrough(events),
    ...chatEvents(rows, queries, outcomes, matched, offset),
    ...runEvents(events),
    ...attemptEvents(events, attempts, submits, tests || [], offset),
    ...awayEvents(events),
    ...activityMarkers(events),
  ].sort(byTime)
}
